import { useEffect, useMemo, useState, type FC } from "react";
import { Checkbox, Label } from "flowbite-react";
import { Dialog, Button } from "@components";
import { useTranslation } from "react-i18next";
import { OrderStatus } from "../../types/order/IOrder";
import { fieldFullWidth } from "@design-system";

export interface UpdateStatusProp {
  show: boolean;
  currentStatus: OrderStatus;
  isLoading: boolean;
  toggleModal: () => void;
  confirmButton: (status: OrderStatus) => void;
}
interface DialogContentProps {
  currentStatus: OrderStatus;
  toggleModal: () => void;
  confirmButton: (status: OrderStatus) => void;
  isLoading: boolean;
}
export const UpdateStatus: FC<UpdateStatusProp> = (props) => {
  return (
    <Dialog
      size="md"
      denseHeader
      show={props.show}
      toggleModal={props.toggleModal}
      hideButtons={true}
      dismissible
      content={
        <DialogContent
          currentStatus={props.currentStatus}
          toggleModal={props.toggleModal}
          confirmButton={props.confirmButton}
          isLoading={props.isLoading}
        />
      }
    />
  );
};

const DialogContent: FC<DialogContentProps> = (props) => {
  const { t } = useTranslation(["common", "orders", "stats"]);

  // Only transitions the backend owns (start/complete/cancel). New and
  // Scheduled are reached through confirm/planning, not by patching status —
  // offering them here would bypass the route lifecycle.
  const availableStatuses = useMemo(
    () =>
      [
        OrderStatus.InProgress,
        OrderStatus.Completed,
        OrderStatus.Cancelled,
      ].filter((status) => status !== props.currentStatus),
    [props.currentStatus],
  );
  const defaultStatus = availableStatuses[0] ?? props.currentStatus;
  const [newStatus, setNewStatus] = useState<OrderStatus>(defaultStatus);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);

  useEffect(() => {
    setNewStatus(defaultStatus);
    setHasAcceptedTerms(false);
  }, [defaultStatus, props.currentStatus]);

  const getStatusTranslationKey = (status: OrderStatus): string => {
    switch (status) {
      case OrderStatus.InProgress:
        return "in_progress";
      case OrderStatus.New:
        return "new";
      default:
        return status.toLowerCase();
    }
  };

  return (
    <div className="flex flex-col items-start justify-between gap-4">
      <p className="text-xl font-medium text-gray-900">
        {t("orders:ongoing_orders.update_status_title")}
      </p>
      <p className="text-md text-muted">
        {t("orders:ongoing_orders.update_status_description")}
      </p>

      <div className="w-full">
        <Label
          className="dark:text-muted"
          htmlFor="new-status"
          value={t("orders:ongoing_orders.update_status_new_status")}
        />
        <form className="w-full">
          <select
            id="new-status"
            className={fieldFullWidth}
            value={newStatus}
            required
            onChange={(e) => setNewStatus(e.target.value as OrderStatus)}
          >
            {availableStatuses.map((status) => (
              <option key={status} value={status}>
                {t(
                  `stats:orderStatus.${getStatusTranslationKey(status)}` as const,
                  status,
                )}
              </option>
            ))}
          </select>
        </form>
      </div>

      <div className="flex items-center gap-2">
        <Checkbox
          id="terms"
          className="text-lgb-primary dark:text-lgb-primary-lighten"
          checked={hasAcceptedTerms}
          onChange={(e) => setHasAcceptedTerms(e.target.checked)}
        />
        <Label htmlFor="terms" className="text-muted font-normal">
          {t("orders:ongoing_orders.update_status_confirmation_message")}
        </Label>
      </div>

      <div className="flex gap-4">
        <Button color="gray" onClick={() => props.toggleModal()}>
          {t("common:close")}
        </Button>
        <Button
          onClick={() => props.confirmButton(newStatus)}
          color="primary"
          disabled={
            !hasAcceptedTerms ||
            newStatus === props.currentStatus ||
            availableStatuses.length === 0 ||
            props.isLoading
          }
        >
          {props.isLoading ? (
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
              {t("common:updating")}
            </div>
          ) : (
            t("common:update")
          )}
        </Button>
      </div>
    </div>
  );
};
